import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import { omit } from '../../../../utilities/misc/omit'
import { ScrollControls } from '../../../ScrollControls'
import styles from './ScrollableWrapper.module.scss'
import {
  determineIfButtonsAreHidden,
  getInlineStyles,
  getScrollState,
  isContainmentSupported,
  useCallbacks,
  useRefs,
  useStates,
} from './ScrollableWrapper.utils'

type Props = {
  children: React.ReactNode
} & HTMLProps<HTMLDivElement>

type ChildProps = {
  className?: string
  ref?: React.Ref<HTMLElement>
  style?: React.CSSProperties
}

export const ScrollableWrapper: FunctionComponent<Props> = ({
  children,
  ...rest
}) => {
  const refs = useRefs()
  const states = useStates(refs)
  const callbacks = useCallbacks({ refs, states })

  const { atStart, atEnd, isOverflowing } = getScrollState({ refs, states })
  const inlineStyles = getInlineStyles({ refs, states, isOverflowing })
  const areButtonsHidden = determineIfButtonsAreHidden(states.maxHeight)

  return (
    <div
      ref={refs.ref.container}
      className={classnames(
        styles.container,
        states.isClient.value && styles.containerClient
      )}
      {...rest}
    >
      <div
        ref={refs.ref.wrapper}
        className={classnames(
          styles.wrapper,
          states.isClient.value && styles.wrapperClient
        )}
        onScroll={callbacks.onWrapperScroll}
      >
        <div
          ref={refs.ref.outer}
          className={classnames(
            styles.outer,
            isOverflowing && styles.outerOverflowing
          )}
          {...omit(callbacks, ['onTraverse', 'onWrapperScroll'])}
        >
          {React.Children.map(children, (child) => {
            if (!React.isValidElement<ChildProps>(child)) {
              return null
            }

            return React.cloneElement(child, {
              ref: refs.ref.inner,
              style: inlineStyles.inner,
              className: classnames(
                child.props.className,
                isOverflowing && styles.shadowOverflowing
              ),
            })
          })}
          {isOverflowing && (
            <>
              <div
                data-testid="table-content-shadow-left"
                className={classnames(
                  styles.shadow,
                  styles.shadowLeft,
                  !atStart && styles.shadowLeftActive,
                  states.isClient.value && styles.shadowClient
                )}
              />

              <div
                data-testid="table-content-shadow-right"
                className={classnames(
                  styles.shadow,
                  styles.shadowRight,
                  !atEnd && styles.shadowRightActive,
                  states.isClient.value && styles.shadowClient
                )}
              />

              {isContainmentSupported() && (
                <div
                  ref={refs.ref.scroller}
                  data-testid="table-scroll-handler"
                  className={styles.scroller}
                  style={inlineStyles.scroller}
                  onScroll={callbacks.onScroll}
                >
                  <div style={inlineStyles.handle} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isOverflowing && (
        <div data-testid="table-scroll-controls" className={styles.overlay}>
          <ScrollControls
            buttonLabel="column"
            scrollPosition={{ atStart, atEnd }}
            buttonClassNames={{
              left: classnames(
                styles.button,
                styles.buttonPrevious,
                areButtonsHidden && styles.buttonLeftHidden
              ),
              right: classnames(
                styles.button,
                styles.buttonNext,
                areButtonsHidden && styles.buttonRightHidden
              ),
            }}
            clickHandler={callbacks.onTraverse}
          />
        </div>
      )}
    </div>
  )
}
